import { useState, useCallback } from "react"
import { Input } from "@/components/ui/input"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination"
import { useFetchPatients } from "@/services/query/patients.query"
import { Button } from "@/components/ui/button"
import { Eye, MoreHorizontal, Pencil, Trash2, MoreVertical } from "lucide-react"
import { useNavigate } from "react-router-dom"
import { AddPatientDialog } from "@/components/AddPatientDialog"
import { EditPatientDialog } from "@/components/EditPatientDialog"
import { DeleteConfirmDialog } from "@/components/DeleteConfirmDialog"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"

function PatientCaseFilesPage() {
  const [pagination, setPagination] = useState({ offset: 0, limit: 10 })
  const [search, setSearch] = useState("")
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [selectedPatient, setSelectedPatient] = useState(null)
  const navigate = useNavigate()

  const { data, isLoading } = useFetchPatients(
    pagination.offset,
    pagination.limit,
    search
  )

  const debouncedSearch = useCallback(
    debounce((value) => {
      setSearch(value)
      setPagination((prev) => ({ ...prev, offset: 0 }))
    }, 500),
    []
  )

  const handlePageChange = (newOffset) => {
    setPagination((prev) => ({ ...prev, offset: newOffset }))
  }

  const handleEdit = (patient) => {
    setSelectedPatient(patient)
    setIsEditDialogOpen(true)
  }

  const handleDelete = (patient) => {
    setSelectedPatient(patient)
    setIsDeleteDialogOpen(true)
  }

  return (
    <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6 lg:py-8">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div className="w-full sm:w-auto sm:flex-1 max-w-sm">
          <Input
            type="search"
            placeholder="Search patients..."
            onChange={(e) => debouncedSearch(e.target.value)}
            className="w-full"
          />
        </div>
        <AddPatientDialog className="w-full sm:w-auto" />
      </div>

      <div className="relative overflow-x-auto rounded-lg border bg-card">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/50">
              <TableHead className="font-semibold">Patient Name</TableHead>
              <TableHead className="font-semibold hidden sm:table-cell">
                Phone Number
              </TableHead>
              <TableHead className="font-semibold hidden md:table-cell">
                Last Modified
              </TableHead>
              <TableHead className="w-[60px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              Array(5)
                .fill(0)
                .map((_, idx) => (
                  <TableRow key={idx} className="animate-pulse">
                    <TableCell className="h-12 bg-gray-100 dark:bg-gray-800" />
                    <TableCell className="hidden sm:table-cell h-12 bg-gray-100 dark:bg-gray-800" />
                    <TableCell className="h-12 bg-gray-100 dark:bg-gray-800" />
                    <TableCell className="hidden md:table-cell h-12 bg-gray-100 dark:bg-gray-800" />
                    <TableCell className="h-12 bg-gray-100 dark:bg-gray-800" />
                  </TableRow>
                ))
            ) : data?.patients.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="h-24 text-center">
                  <p className="text-gray-500 dark:text-gray-400">
                    No patients found
                  </p>
                </TableCell>
              </TableRow>
            ) : (
              data?.patients.map((patient) => (
                <TableRow
                  key={patient.thread_id}
                  className="hover:bg-muted/50 cursor-pointer"
                  onClick={() => navigate(`/patients/${patient.thread_id}`)}
                >
                  <TableCell className="font-medium max-w-[200px]">
                    <span className="line-clamp-1">{patient.user_name}</span>
                  </TableCell>
                  <TableCell className="hidden sm:table-cell">
                    <span className="text-sm text-muted-foreground">
                      {patient.phone_number}
                    </span>
                  </TableCell>
                  <TableCell className="hidden md:table-cell">
                    <span className="text-sm text-muted-foreground">
                      {new Date(patient.modified_at).toLocaleDateString()}
                    </span>
                  </TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <MoreVertical className="h-4 w-4" />
                          <span className="sr-only">Open menu</span>
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-[160px]">
                        <DropdownMenuItem
                          onClick={() =>
                            navigate(`/patients/${patient.thread_id}`)
                          }
                        >
                          <Eye className="mr-2 h-4 w-4" />
                          View Details
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleEdit(patient)}>
                          <Pencil className="mr-2 h-4 w-4" />
                          Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => handleDelete(patient)}
                          className="text-red-600 dark:text-red-400"
                        >
                          <Trash2 className="mr-2 h-4 w-4" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {data && data.pagination.totalPages > 1 && (
        <div className="mt-6 flex justify-center">
          <Pagination>
            <PaginationContent className="flex-wrap gap-2">
              <PaginationItem className="hidden sm:block">
                <PaginationPrevious
                  onClick={() =>
                    handlePageChange(
                      Math.max(0, pagination.offset - pagination.limit)
                    )
                  }
                  disabled={pagination.offset === 0}
                />
              </PaginationItem>
              {[...Array(data.pagination.totalPages)].map((_, idx) => (
                <PaginationItem
                  key={idx}
                  className={
                    idx > 2 && idx < data.pagination.totalPages - 1
                      ? "hidden sm:block"
                      : ""
                  }
                >
                  <PaginationLink
                    onClick={() => handlePageChange(idx * pagination.limit)}
                    isActive={pagination.offset === idx * pagination.limit}
                    className="min-w-[40px] justify-center"
                  >
                    {idx + 1}
                  </PaginationLink>
                </PaginationItem>
              ))}
              <PaginationItem className="hidden sm:block">
                <PaginationNext
                  onClick={() =>
                    handlePageChange(pagination.offset + pagination.limit)
                  }
                  disabled={
                    pagination.offset + pagination.limit >=
                    data.pagination.total
                  }
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>
      )}

      {selectedPatient && (
        <>
          <EditPatientDialog
            patient={selectedPatient}
            open={isEditDialogOpen}
            onOpenChange={setIsEditDialogOpen}
            onClose={() => setSelectedPatient(null)}
          />
          <DeleteConfirmDialog
            patient={selectedPatient}
            open={isDeleteDialogOpen}
            onOpenChange={setIsDeleteDialogOpen}
            onClose={() => setSelectedPatient(null)}
          />
        </>
      )}
    </div>
  )
}

function debounce(func, wait) {
  let timeout
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout)
      func(...args)
    }
    clearTimeout(timeout)
    timeout = setTimeout(later, wait)
  }
}

export default PatientCaseFilesPage
